import React, { useEffect, useMemo, useRef, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import projects from '../../data/projects';

const BrowserFrame = ({ image, label, secondary = false, eager = false }) => (
  <div className={`product-browser ${secondary ? 'product-browser--secondary' : ''}`}>
    <div className="product-browser__bar" aria-hidden="true">
      <div className="product-browser__dots">
        <span />
        <span />
        <span />
      </div>
      <span className="product-browser__address">{label}</span>
      <span className="product-browser__secure" />
    </div>
    <img
      src={image.src}
      alt={image.alt}
      loading={eager ? 'eager' : 'lazy'}
      draggable="false"
    />
  </div>
);

const PhoneDeck = ({ images, eager = false }) => (
  <div className="product-phone-deck">
    {images.map((image, index) => (
      <div key={image.src} className={`product-phone product-phone--${index + 1}`}>
        <div className="product-phone__speaker" aria-hidden="true" />
        <img
          src={image.src}
          alt={image.alt}
          loading={eager ? 'eager' : 'lazy'}
          draggable="false"
        />
      </div>
    ))}
  </div>
);

const ProjectVisual = ({ project, eager = false }) => {
  if (project.visual.kind === 'phones') {
    return <PhoneDeck images={project.visual.images} eager={eager} />;
  }

  return (
    <div className="product-browser-stack">
      <BrowserFrame
        image={project.visual.images[0]}
        label={project.visual.label}
        eager={eager}
      />
      {project.visual.images[1] && (
        <BrowserFrame image={project.visual.images[1]} label={project.name} secondary />
      )}
    </div>
  );
};

const ProductShowcase = () => {
  const [activeId, setActiveId] = useState(projects[0].id);
  const stageRef = useRef(null);
  const reduceMotion = useReducedMotion();
  const activeIndex = projects.findIndex((project) => project.id === activeId);
  const activeProject = useMemo(
    () => projects.find((project) => project.id === activeId) || projects[0],
    [activeId]
  );

  useEffect(() => {
    projects.flatMap((project) => project.visual.images).forEach((image) => {
      const preload = new Image();
      preload.src = image.src;
    });
  }, []);

  const handlePointerMove = (event) => {
    if (reduceMotion || event.pointerType !== 'mouse' || !stageRef.current) return;

    const rect = stageRef.current.getBoundingClientRect();
    const x = (event.clientX - rect.left) / rect.width;
    const y = (event.clientY - rect.top) / rect.height;
    stageRef.current.style.setProperty('--scene-x', `${(x - 0.5) * 2.4}deg`);
    stageRef.current.style.setProperty('--scene-y', `${(0.5 - y) * 1.8}deg`);
    stageRef.current.style.setProperty('--shift-x', `${(x - 0.5) * 8}px`);
    stageRef.current.style.setProperty('--shift-y', `${(y - 0.5) * 6}px`);
  };

  const resetPerspective = () => {
    if (!stageRef.current) return;
    stageRef.current.style.setProperty('--scene-x', '0deg');
    stageRef.current.style.setProperty('--scene-y', '0deg');
    stageRef.current.style.setProperty('--shift-x', '0px');
    stageRef.current.style.setProperty('--shift-y', '0px');
  };

  return (
    <div
      className="product-showcase"
      data-testid="product-showcase"
      style={{
        '--project-accent': activeProject.accent,
        '--project-accent-rgb': activeProject.accentRgb,
      }}
    >
      <div
        ref={stageRef}
        className="product-showcase__stage"
        onPointerMove={handlePointerMove}
        onPointerLeave={resetPerspective}
        role="group"
        aria-label={`${activeProject.name}: ${activeProject.type}`}
      >
        <div className="product-showcase__grid" aria-hidden="true" />

        <AnimatePresence initial={false} mode="sync">
          <motion.div
            key={activeProject.id}
            className="product-showcase__visual"
            initial={reduceMotion ? false : { opacity: 0, y: 12, scale: 0.985 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={reduceMotion ? { opacity: 0 } : { opacity: 0, y: -8, scale: 1.01 }}
            transition={{ duration: 0.38, ease: [0.19, 1, 0.22, 1] }}
          >
            <div className="product-showcase__perspective">
              <ProjectVisual project={activeProject} eager={activeIndex === 0} />
            </div>
          </motion.div>
        </AnimatePresence>

        <div className="product-showcase__caption" aria-live="polite">
          <span className="product-showcase__caption-dot" aria-hidden="true" />
          <span className="product-showcase__caption-name">{activeProject.name}</span>
          <span className="product-showcase__caption-type">{activeProject.type}</span>
          <span className="product-showcase__caption-count">
            {String(activeIndex + 1).padStart(2, '0')} / {String(projects.length).padStart(2, '0')}
          </span>
        </div>
      </div>

      <nav className="product-showcase__tabs" aria-label="Choose a product to preview">
        {projects.map((project, index) => {
          const isActive = project.id === activeId;
          return (
            <button
              key={project.id}
              type="button"
              aria-pressed={isActive}
              onClick={() => setActiveId(project.id)}
              className={`product-showcase__tab ${isActive ? 'product-showcase__tab--active' : ''}`}
            >
              <span>{String(index + 1).padStart(2, '0')}</span>
              {project.name}
            </button>
          );
        })}
      </nav>
    </div>
  );
};

export default ProductShowcase;
